import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import i18n from 'i18next';

import { Button, ButtonGroup } from 'uikit/buttons';
import { Drawer } from 'uikit/layouts';
import { Menu } from 'uikit/menus';
import { Typo } from 'uikit/content';

import { useUrlSearch } from '@/search';

import getMenuItems from '../helpers/getMenuItems';

import Filter from './filter';

import styles from '../styles/filterableDrawer.module.scss';

const FilterableDrawer = ({
  filters = [],
  isOpen = false,
  onClose = () => {},
  triggerRef = null,
}) => {
  const urlSearch = useUrlSearch();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const hasSelectedFilters = Boolean(selectedFilters.length);

  const filterMap = useMemo(
    () => Object.fromEntries(filters.map((filter) => [filter.key, filter])),
    [filters],
  );

  const handleAddClick = (key) => {
    setSelectedFilters((prevFilters) => [...prevFilters, { key, values: [] }]);
  };

  const handleSelectionUpdate = useCallback(({ isSelected, isSingleSelect, key, value }) => {
    setSelectedFilters((prevFilters) =>
      prevFilters.map((prevFilter) => {
        if (prevFilter.key !== key) return prevFilter;
        if (isSingleSelect) return { ...prevFilter, values: isSelected ? [value] : [] };

        return {
          ...prevFilter,
          values: isSelected
            ? [...prevFilter.values, value]
            : prevFilter.values.filter((prevValue) => prevValue !== value),
        };
      }),
    );
  }, []);

  const handleRemoveClick = useCallback((key) => {
    setSelectedFilters((prevFilters) => prevFilters.filter((filter) => filter.key !== key));
  }, []);

  const handleClearAllClick = () => {
    setSelectedFilters([]);
  };

  const activeFilters = useMemo(
    () =>
      filters.reduce((a, { field_type: type, key }) => {
        const values = urlSearch.getValues(key).flatMap((value) => {
          if (!value) return [];

          return type === 'Integer' ? Number(value) : value;
        });

        return values.length ? [...a, { key, values }] : a;
      }, []),
    [filters, urlSearch],
  );

  const availableFilters = useMemo(
    () => filters.filter(({ key }) => !selectedFilters.some((selected) => selected.key === key)),
    [filters, selectedFilters],
  );

  useEffect(() => {
    if (!isOpen) return setHasInitialized(false);
    if (hasInitialized) return;

    setSelectedFilters(activeFilters);
    setHasInitialized(true);
  }, [activeFilters, hasInitialized, isOpen]);

  useEffect(() => {
    const getQueryString = (items) => {
      const params = urlSearch.getParams([]);

      items.forEach(({ key, values }) => values.forEach((value) => params.append(key, value)));

      return params.toString();
    };

    setIsApplyDisabled(getQueryString(activeFilters) === getQueryString(selectedFilters));
  }, [activeFilters, selectedFilters, urlSearch]);

  const handleApplyClick = () => {
    const params = urlSearch.getParams();

    filters.forEach(({ key }) => {
      params.delete(key);

      const { values } = selectedFilters.find((filter) => filter.key === key) || {};

      values?.forEach((value) => params.append(key, value));
    });

    params.delete('page');
    urlSearch.applyParams(params);
    onClose();
  };

  const selectedFilterRows = useMemo(
    () =>
      selectedFilters.map(({ key, values }) => {
        const { label, ...filter } = filterMap[key];

        return (
          <li className={styles.filter} key={key}>
            <Filter
              dropdownPosition="left"
              extraParams={filter.extraParams}
              isAsync={filter.is_async}
              isFullWidth
              isSingleSelect={!filter.allow_multiple_selects}
              name={label}
              onSelectionUpdate={handleSelectionUpdate}
              options={filter.options}
              param={key}
              selection={values}
              variant="drawer"
            />
            <Button
              ariaLabel={i18n.t('remove-label', { label })}
              icon={<DeleteOutlinedIcon />}
              onClick={() => handleRemoveClick(key)}
              size="small"
              variant="ghost"
            />
          </li>
        );
      }),
    [filterMap, handleRemoveClick, handleSelectionUpdate, selectedFilters],
  );

  return (
    <Drawer
      className={styles.container}
      hasCloseButton
      id="filter-drawer"
      isActive={isOpen}
      isSocial={false}
      onCloseButtonClick={onClose}
      placement="right"
      triggerRef={triggerRef}
    >
      <div className={styles.element}>
        <div className={styles.header}>
          <Typo variant="d01">{i18n.t('filters')}</Typo>
          <Typo color="secondary" variant="b01">
            {i18n.t('filter-drawer-body')}
          </Typo>
        </div>
        <div className={styles.filters}>
          {hasSelectedFilters && <ul className={styles.filters}>{selectedFilterRows}</ul>}
          <ButtonGroup>
            <Menu
              component={
                <Button
                  icon={<AddOutlinedIcon />}
                  isDisabled={!availableFilters.length}
                  size="small"
                  text={i18n.t('add-a-filter')}
                  variant="outlined"
                />
              }
              horizontalPosition="left"
              variant="unbounded"
            >
              {getMenuItems(
                availableFilters.map(({ key, label }) => ({
                  key,
                  onClick: () => handleAddClick(key),
                  text: label,
                })),
              )}
            </Menu>
            {hasSelectedFilters && (
              <Button
                onClick={handleClearAllClick}
                size="small"
                text={i18n.t('clear-all')}
                variant="ghost"
              />
            )}
          </ButtonGroup>
        </div>
        <ButtonGroup>
          <Button
            color="brand"
            isDisabled={isApplyDisabled}
            onClick={handleApplyClick}
            size="small"
            text={i18n.t('apply')}
          />
          <Button onClick={onClose} size="small" text={i18n.t('cancel')} variant="ghost" />
        </ButtonGroup>
      </div>
    </Drawer>
  );
};

FilterableDrawer.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      allow_multiple_selects: PropTypes.bool,
      extraParams: PropTypes.shape({}),
      field_type: PropTypes.string,
      is_async: PropTypes.bool,
      key: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
        }),
      ),
    }),
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default React.memo(FilterableDrawer);
